import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useQueryClient } from 'react-query';
import {
    checkIfShiftAm,
    customDeepClone,
    days,
    getDateOfWeekday,
    getFirstLetterUpperCase,
    getFirstThreeLetter,
    getStaffAvailabilityForTheDay,
    getTimeOffMessage,
    isDateInRange,
    JobRoleForShift,
    PublishedSchedule,
    ScheduleData,
    ScheduleMetadata,
    Staff,
    StyleDetails,
} from '../../../../../helpers/venueScheduleHelper';
import CustomTooltip from './CustomTooltip';
import '../venueSchedule.scss';
import { createElement } from 'react';
import CustomEventListForTable from './CustomEventListForTable';
import { Badge } from 'primereact/badge';
import UserAvatar from './UserAvatar';
import { _DAYJS as dayjs } from '../../../../../config/dayjs.config';
import noPublishedSchedule from '../../../../../assets/images/no_published_schedule.svg';
import { Divider } from 'primereact/divider';

type Props = {
    isLoading: boolean;
    selectedWeek: dayjs.Dayjs;
    staffsWithSchedule: ScheduleData | null;
    // swapShiftRequest: (shift: PublishedSchedule, staff: Staff) => void;
    parent?: string;
    getRoleColorCode: (roleId: string) => StyleDetails;
    scheduleMetadata: ScheduleMetadata | null;
    viewType?: 'amPm' | 'normal';
};

const ScheduleDesktopView = ({
    isLoading,
    selectedWeek,
    staffsWithSchedule,
    parent,
    getRoleColorCode,
    scheduleMetadata,
    viewType,
}: Readonly<Props>) => {
    const queryClient = useQueryClient();
    // const staffId = getStaffId()
    const { staffs, mandatoryDays, holidays } = staffsWithSchedule ?? { staffs: [], mandatoryDays: [], holidays: [] };
    if (!staffsWithSchedule) {
        return (
            <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                <div className="flex-grow-1 align-items-center justify-content-center p-3 text-center mt-5">
                    <img src={noPublishedSchedule} alt="no published schedule" style={{ textAlign: 'center' }} />
                    <h5 className="mt-2 mb-3">No published schedule for the current week</h5>
                </div>
            </div>
        );
    }

    const totalEvents = scheduleMetadata?.customEvents?.map((event) => event?.eventData)?.flat().length ?? 0;
    // const staffSortByCurrentUser = staffs.reduce<Staff[]>((staffArray, staff) => {
    //     if (staff.id === staffId) {
    //         return [staff, ...staffArray];
    //     } else {
    //         return [...staffArray, staff];
    //     }
    // }, [])
    // .filter((staff) => Object.values(staff.shifts).length);// Filter staff list if no shifts assigned/scheduled and sort with first name

    // if (parent === 'myScheduleView') {
    //     staffSortByCurrentUser.sort((a, b) => a.firstName.localeCompare(b.firstName));
    // }

    // const managePermission = checkIfManageSwapRequest(queryClient)

    const staffNameTemplate = (rowData: Staff) => {
        const { preferredName = '', jobRoles = [], photoThumbnail } = rowData ?? {};
        const roles = jobRoles
            .map((role: JobRoleForShift | undefined) => role?.jobRole?.jobRoleName)
            .filter(Boolean)
            .join(', ');

        return (
            <div className="text-nowrap d-flex">
                <UserAvatar imageUrl={photoThumbnail} name={preferredName} />
                <div className="text-nowrap">
                    <div className="overflow-ellipsis fw-bold">{preferredName}</div>
                    <div className="overflow-ellipsis" title={roles}>
                        {roles || 'No roles assigned'}
                    </div>
                </div>
            </div>
        );
    };

    const cellBodyTemplate = ({ day, rowData }: { day: string; rowData: Staff }) => {
        const { shifts, preferredName, unavailableDays, availabilities } = rowData;
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
        const availabilityText = getStaffAvailabilityForTheDay({ day, availabilities, selectedWeek, type: 'schedule' });

        const unavailabilityArray = unavailableDays.filter((unavailableDay) =>
            isDateInRange({ date: date, startDate: unavailableDay.startDate, endDate: unavailableDay.endDate })
        );
        const unavailability = unavailabilityArray.filter((item) => item.actionStatus === 'APPROVED' || item.actionStatus === 'PENDING');
        const shiftsArray = shifts[date];

        const timeOffText = getTimeOffMessage({ unavailability: unavailability, type: 'schedule' }, date)?.length
            ? getTimeOffMessage({ unavailability: unavailability, type: 'schedule' }, date)
            : availabilityText;

        if (shiftsArray?.length) {
            // Sort the shiftsArray based on startTime and endTime
            const sortedShifts = shiftsArray.sort((a, b) => {
                const startA = dayjs(a.assignedShifts.startTime, 'hh:mm A');
                const startB = dayjs(b.assignedShifts.startTime, 'hh:mm A');
                const endA = dayjs(a.assignedShifts.endTime, 'hh:mm A');
                const endB = dayjs(b.assignedShifts.endTime, 'hh:mm A');

                // Compare start times first; if they're equal, compare end times
                if (startA.isSame(startB)) {
                    return endA.isBefore(endB) ? -1 : 1;
                }
                return startA.isBefore(startB) ? -1 : 1;
            });

            const shiftTemplate = createElement('span', { children: [<></>] });
            const shiftAMTemplate = createElement('div', {
                className: 'w-100 flex-grow-1 d-flex flex-column align-items-center',
                children: [<></>],
            });
            const shiftPMTemplate = createElement('div', {
                className: 'w-100 flex-grow-1 d-flex flex-column align-items-center',
                children: [<></>],
            });

            sortedShifts.forEach((shift, index) => {
                const updatedShift = customDeepClone(shift) as PublishedSchedule;
                const { startTime, endTime, isBd, isClose, role, isPoolRequested, roleId } = updatedShift.assignedShifts;

                const styleDetails = getRoleColorCode(roleId);
                const { backgroundColor, color } = styleDetails;

                const parsedDate = getDateOfWeekday({ selectedWeek, day, format: 'YYYY-MM-DD' });
                const isAmShift = checkIfShiftAm(startTime);

                const startTimeParsed = dayjs(startTime, 'hh:mm A');
                const endTimeParsed = dayjs(endTime, 'hh:mm A');

                const shiftCustomTemplate = (
                    <CustomTooltip
                        key={`swap-custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                        staffName={preferredName}
                        showDelay={500}
                        content={sortedShifts}
                        showOnlyHovered={true}
                        currentShift={shift}
                    >
                        <div
                            id={`custom-tooltip-${rowData?.id}-${shift?.id}-${parsedDate}`}
                            className="scheduler-shift-card-body"
                            style={{
                                backgroundColor: 'white',
                                border: `2px solid ${backgroundColor}`,
                            }}
                            onClick={() => {
                                // if (parent !== 'myScheduleView') {
                                //     if (canRequestSwap) return
                                //     swapShiftRequest(updatedShift, rowData)
                                // }
                            }}
                        >
                            <div className="d-flex">
                                <div className="flex-grow-0 d-flex align-items-center justify-center">
                                    <span
                                        className="shift-role"
                                        style={{
                                            backgroundColor,
                                            color: color,
                                        }}
                                    >
                                        {getFirstLetterUpperCase(role ?? '')}
                                    </span>
                                </div>
                                <div className="flex-grow-1 shift-name-main text-nowrap" role="button">
                                    {viewType === 'amPm' ? (
                                        <>
                                            {isClose
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - CL`
                                                : ''}
                                            {isBd
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - V`
                                                : ''}
                                            {!isBd && !isClose
                                                ? `${
                                                      startTimeParsed.minute() === 0
                                                          ? startTimeParsed.format('h')
                                                          : startTimeParsed.format('h:m')
                                                  } - ${
                                                      endTimeParsed.minute() === 0 ? endTimeParsed.format('h') : endTimeParsed.format('h:m')
                                                  }`
                                                : ''}
                                        </>
                                    ) : (
                                        <>
                                            {isClose ? `${startTime} - Close` : ''}
                                            {isBd ? `${startTime} - V` : ''}
                                            {!isBd && !isClose ? `${startTime} - ${endTime}` : ''}
                                        </>
                                    )}
                                </div>
                                {parent !== 'myScheduleView' && isPoolRequested && (
                                    <div className="flex-grow-0 d-flex align-items-center justify-center swap-icn-bg">
                                        <i className="pi pi-arrow-right-arrow-left text-light mx-1"></i>
                                    </div>
                                )}
                            </div>
                        </div>
                    </CustomTooltip>
                );

                if (viewType === 'amPm') {
                    if (isAmShift) {
                        shiftAMTemplate.props.children.push(shiftCustomTemplate);
                    } else {
                        shiftPMTemplate.props.children.push(shiftCustomTemplate);
                    }
                } else {
                    shiftTemplate.props.children.push(shiftCustomTemplate);
                }
            });

            return viewType === 'amPm' ? (
                <>
                    {timeOffText}
                    <div className="d-flex gap-1">
                        {shiftAMTemplate}
                        {shiftPMTemplate}
                    </div>
                </>
            ) : (
                <>
                    {timeOffText}
                    {shiftTemplate}
                </>
            );
        } else {
            return timeOffText ?? <></>;
        }
    };

    const getAmPmStaffCount = (day: string) => {
        const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
        const filteredStaffs = staffsWithSchedule?.staffs.filter((staff) => Object.keys(staff.shifts).length > 0);
        const filteredStaffsByDay = filteredStaffs?.filter((staff) => staff.shifts[date]?.length > 0);

        let amCount = 0;
        let pmCount = 0;

        filteredStaffsByDay?.forEach((staff) => {
            const shifts = staff?.shifts[date];
            const amShifts = shifts?.filter((shift) => shift.assignedShifts.startTime.includes('AM'));
            const pmShifts = shifts?.filter((shift) => shift.assignedShifts.startTime.includes('PM'));
            amCount += amShifts?.length ? 1 : 0;
            pmCount += pmShifts?.length ? 1 : 0;
        });
        return { am: amCount, pm: pmCount };
    };

    const renderColumns = () => {
        return days.map((day, index) => {
            const amPmCount = getAmPmStaffCount(day);
            return (
                <Column
                    key={`${day}-${index + 1}`}
                    headerClassName="workTimeScheduleHeader p-0"
                    header={() => {
                        const checkHoliday = () => {
                            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
                            const isHolidays =
                                holidays.filter((holiday) =>
                                    isDateInRange({ date: date, startDate: holiday.startDate, endDate: holiday.endDate })
                                ).length > 0;
                            return isHolidays;
                        };

                        const checkMandatoryDays = () => {
                            const date = getDateOfWeekday({ day, selectedWeek, format: 'YYYY-MM-DD' });
                            const isMandatory =
                                mandatoryDays.filter((mandatoryDay) =>
                                    isDateInRange({ date: date, startDate: mandatoryDay.startDate, endDate: mandatoryDay.endDate })
                                ).length > 0;
                            return isMandatory;
                        };

                        return (
                            <div className="d-flex flex-column align-items-center justify-content-center position-relative">
                                <div className="scheduler-header w-100" style={{ height: `120px` }}>
                                    <div style={{ height: `55px` }}>
                                        <div className="d-flex  justify-content-center">
                                            <div className="mb-0 text-center text-nowrap fw-normal text-muted">
                                                {getFirstThreeLetter(day)}
                                            </div>
                                            <div className="mb-2 text-center text-nowrap">
                                                , {getDateOfWeekday({ selectedWeek, day: day })}
                                            </div>
                                        </div>
                                        {/* rendering holidays */}
                                        {checkHoliday() && (
                                            <div className={`${checkHoliday() && 'holiday-header'}`}>
                                                <div className="mb-1 text-center text-nowrap fw-normal text-muted w-100">
                                                    <div className="d-flex align-center justify-content-center position-relative">
                                                        <div>Holiday</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* rendering mandatory days */}
                                        {checkMandatoryDays() && (
                                            <div className={`${checkMandatoryDays() && 'mandatory-day'}`}>
                                                <div className="mb-1 text-center text-nowrap fw-normal text-muted w-100">
                                                    <div className="d-flex align-center justify-content-center position-relative">
                                                        <div>Mandatory Day</div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <CustomEventListForTable customEvents={scheduleMetadata?.customEvents ?? []} day={day} />
                                </div>
                                {viewType === 'amPm' && (
                                    <div className="w-100 row border-top" style={{ height: '30px' }}>
                                        <div className="col-6 d-flex align-items-center justify-content-center">
                                            <span>AM</span>
                                            <Badge
                                                value={amPmCount?.am}
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                                className="ms-1"
                                            />
                                        </div>
                                        <div className="col-6 d-flex align-items-center justify-content-center">
                                            <span>PM</span>
                                            <Badge
                                                value={amPmCount?.pm}
                                                style={{ fontSize: '0.65rem', minWidth: '1.05rem', height: '1rem', lineHeight: '1rem' }}
                                                className="ms-1"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }}
                    body={(data) => cellBodyTemplate({ day, rowData: data })}
                    style={{ minWidth: `${viewType === 'amPm' ? '15rem' : '12rem'}` }}
                    className="shift-column"
                />
            );
        });
    };

    const tableHeader = () => {
        return (
            <div
                className="w-100 d-flex flex-column align-items-center justify-content-center"
                style={{ minWidth: `${viewType === 'amPm' ? '15rem' : '12rem'}` }}
            >
                <div className="w-100 px-4 d-flex justify-content-start align-items-center" style={{ height: '40px' }}>
                    {/* <Checkbox
                        inputId="select-all-users"
                        name="select-all"
                        onChange={(e) => e.checked ? setSelectedUsers(allUsers) : setSelectedUsers([])}
                        checked={selectedUsers.length !== 0 && selectedUsers.length === allUsers.length}
                    />
                    <label htmlFor="select-all-users" className="mx-2">
                        <JTranslation text={"Select All"} />
                    </label> */}
                </div>
                <Divider className="p-0 m-0" />
                <div className="w-100 d-flex justify-content-between align-items-center px-4" style={{ height: '65px' }}>
                    Events
                    <Badge className="ms-2" value={totalEvents} />
                </div>

                {viewType === 'amPm' && (
                    <>
                        <Divider className="p-0 m-0 py-1" />
                        <div className="w-100 d-flex justify-content-between align-items-center px-4 pt-3" style={{ height: '13px' }}>
                            Shifts
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        // <MlxBlockUI isLoading={isLoading}>
        <div className="card h-100">
            <DataTable
                value={staffs}
                showGridlines
                scrollable
                scrollHeight="96%"
                className={`h-100 work-time-schedule-table ${parent !== 'myScheduleView' ? 'bg-active-user' : ''}`}
                loading={isLoading}
                pt={{ loadingOverlay: { className: 'color-primary-custom', style: { zIndex: 3 } } }}
            >
                <Column
                    field="day"
                    className="overflow-ellipsis"
                    header={tableHeader}
                    body={staffNameTemplate}
                    pt={{
                        headerTitle: {
                            style: {
                                padding: '0px',
                                width: '100%',
                            },
                        },
                        headerCell: {
                            style: {
                                padding: '0px',
                            },
                        },
                    }}
                />
                {renderColumns()}
            </DataTable>
        </div>
        // </MlxBlockUI>
    );
};

export default ScheduleDesktopView;
